import * as React from 'react'

import {
  FileImageIcon,
  FileTextIcon,
  UploadIcon,
  WandSparklesIcon,
  XIcon,
} from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { SLASH } from '~/client/src/shared/utils/usefulStrings'

import FormGeneralOptionsDropdown from '../../../../FormTypeConfigurator/components/GeneralFormConfigurator/components/FormGeneralOptionsDropdown'
import SmartFormImportStore from '../SmartFormImport.store'
import SmartFormImportCard from '../SmartFormImportCard/SmartFormImportCard'

import './SmartFormImportPopover.scss'

interface IProps {
  store: SmartFormImportStore
}

const ALLOWED_TYPES = ['.pdf', '.jpg', '.jpeg', '.png']
const PDF = 'pdf'

@observer
export default class SmartFormImportPopover extends React.Component<
  IProps,
  { isOpen: boolean }
> {
  private filePicker = React.createRef<IFilePickerActions>()

  public state = { isOpen: false }

  public constructor(props: IProps) {
    super(props)
  }

  public render() {
    return (
      <Popover open={this.state.isOpen} onOpenChange={this.setOpen}>
        <PopoverTrigger render={<Button type="button" variant="ghost" />}>
          <WandSparklesIcon />
          {Localization.translator.smartFormImport}
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="smart-form-import-popover tw:w-[400px] tw:overflow-hidden tw:p-0"
        >
          {this.popoverContent}
        </PopoverContent>
      </Popover>
    )
  }

  private get popoverContent(): JSX.Element {
    const {
      file,
      workflowName,
      categoryId,
      categoriesOptions,
      areAllFieldsValid,
      handleWorkflowNameChange,
      handleCategoryChange,
      handleFileSelect,
      importFile,
    } = this.props.store

    return (
      <SmartFormImportCard onClose={this.close}>
        <div className="popover-content">
          <div className="popover-description">
            {Localization.translator.smartFormImportDescription}
          </div>

          {!file ? (
            this.fileInput()
          ) : (
            <div className="col mb15 capitalize">
              {this.filePreview()}
              <div className="input-fields-container">
                <StruxhubInput
                  label={Localization.translator.formName}
                  isRequired
                  value={workflowName}
                  onChange={handleWorkflowNameChange}
                />
                <FormGeneralOptionsDropdown
                  targetClassName="full-width"
                  customSelectorLabel={Localization.translator.workflowCategory}
                  isRequiredTextShown
                  shouldHighlightInvalid
                  selectedValue={categoryId}
                  options={categoriesOptions}
                  onItemClick={handleCategoryChange}
                />
              </div>
            </div>
          )}

          <button
            type="button"
            className="import-file-button"
            disabled={!areAllFieldsValid}
            onClick={() => {
              void importFile()
              this.close()
            }}
          >
            {Localization.translator.importFile}
          </button>

          <FilePicker
            actionsRef={this.filePicker}
            accept={ALLOWED_TYPES.join(',')}
            onPick={([file]) => handleFileSelect(file)}
          />
        </div>
      </SmartFormImportCard>
    )
  }

  private filePreview(): JSX.Element {
    const { file, handleRemoveFile } = this.props.store
    const fileType = file.type.split(SLASH)[1]

    return (
      <div className="file-preview">
        <div className="no-grow">
          {fileType === PDF ? (
            <FileTextIcon className="tw:size-9 tw:text-muted-foreground" />
          ) : (
            <FileImageIcon className="tw:size-9 tw:text-muted-foreground" />
          )}
        </div>
        <div className="full-width file-name-container">
          <div className="text-ellipsis file-name">{file.name}</div>
          <div className="grey file-extension">{fileType.toUpperCase()}</div>
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          className="remove-file-button tw:text-muted-foreground"
          aria-label={Localization.translator.remove}
          onClick={handleRemoveFile}
        >
          <XIcon />
        </Button>
      </div>
    )
  }

  private fileInput(): JSX.Element {
    const {
      isDragOver,
      fileValidationMessage,
      handleDrop,
      handleDragEnter,
      handleDragLeave,
    } = this.props.store

    return (
      <>
        <div
          className={classList({
            'drag-drop-area': true,
            'drag-over': isDragOver,
            invalid: !!fileValidationMessage,
          })}
          onDrop={handleDrop}
          onDragOver={this.handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onClick={this.handleChooseFile}
        >
          <div className="upload-icon">
            <UploadIcon className="tw:size-6" />
          </div>
          <div className="drag-drop-text">
            <span>{Localization.translator.dragAndDropFile}</span>
            <span className="tw:mx-2">{Localization.translator.or}</span>
            <span className="choose-file-link">
              {Localization.translator.chooseFile}
            </span>
          </div>
        </div>
        <div className="invalid-file-text">{fileValidationMessage}</div>
        <div className="file-support-text">
          {Localization.translator.fileSupportText}
        </div>
      </>
    )
  }

  private setOpen = (isOpen: boolean) => this.setState({ isOpen })

  private close = () => this.setOpen(false)

  private handleChooseFile = (): void => {
    this.filePicker.current?.open()
  }

  private handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
  }
}
